import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  CalendarIcon,
  CheckCircledIcon,
  ClockIcon,
  FileTextIcon,
  InfoCircledIcon,
  StarFilledIcon,
} from '@radix-ui/react-icons';
import Navbar from '../components/Navbar';
import EscalaGrade from '../components/escala/EscalaGrade';
import ConfirmModal from '../components/escala/ConfirmModal';
import {
  CICLO_STATUS_LABEL,
  DIAS_SEMANA,
  DIAS_SEMANA_CURTO,
  MODO_LABEL,
  TURNO_LABEL,
  formatDataCurta,
  mensagemErro,
  nomeEscala,
  periodoLabel,
  fimDeSemanaComVaga,
  turnosDoCiclo,
} from '../components/escala/escalaUtils';
import { desistir, inscrever, listarCiclos, obterCiclo } from '../services/escalaApi';
import './Global.css';
import './Escala.css';

/**
 * Tela do itinerante: o calendário publicado, com os nomes já confirmados e
 * o botão para pedir a vaga. Quem não é itinerante vê a escala, sem botões.
 */
const EscalaPage = () => {
  const [ciclos, setCiclos] = useState([]);
  const [cicloId, setCicloId] = useState(null);
  const [ciclo, setCiclo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [carregandoCiclo, setCarregandoCiclo] = useState(false);
  const [busy, setBusy] = useState(null); // id do turno/inscrição em ação
  const [mostrarFds, setMostrarFds] = useState(null);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const lista = await listarCiclos();
        setCiclos(lista);
        const emCurso = lista.find((c) => c.emCurso) || lista.find((c) => c.status === 'publicado') || lista[0];
        setCicloId(emCurso?.id || null);
      } catch (e) {
        toast.error(mensagemErro(e, 'Erro ao carregar a escala.'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const carregarCiclo = useCallback(async (id) => {
    if (!id) return;
    setCarregandoCiclo(true);
    try {
      setCiclo(await obterCiclo(id));
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro ao carregar o ciclo.'));
    } finally {
      setCarregandoCiclo(false);
    }
  }, []);

  useEffect(() => {
    carregarCiclo(cicloId);
  }, [cicloId, carregarCiclo]);

  const turnos = useMemo(() => (ciclo ? turnosDoCiclo(ciclo.dias) : []), [ciclo]);
  const fdsAuto = useMemo(() => (ciclo ? fimDeSemanaComVaga(ciclo.dias) : { sabado: false, domingo: false }), [ciclo]);
  const mostrar = mostrarFds === null ? fdsAuto : { sabado: mostrarFds, domingo: mostrarFds };
  const mostrarFdsEfetivo = mostrar.sabado && mostrar.domingo;

  const minhasInscricoes = useMemo(() => {
    if (!ciclo) return [];
    const out = [];
    for (const dia of ciclo.dias) {
      for (const turno of dia.turnos) {
        if (turno.minha && (turno.minha.status === 'confirmado' || turno.minha.status === 'pendente')) {
          out.push({ ...turno.minha, dia, turno });
        }
      }
    }
    return out;
  }, [ciclo]);

  const aberto = ciclo?.status === 'publicado';

  const pedir = async (dia, turno) => {
    const chave = `${dia.id}:${turno.turno}`;
    setBusy(chave);
    try {
      const r = await inscrever(ciclo.id, dia.id, turno.turno);
      if (r.status === 'confirmado') {
        toast.success(`Vaga garantida: ${DIAS_SEMANA_CURTO[dia.diaSemana]} ${formatDataCurta(dia.data)}, ${TURNO_LABEL[turno.turno].toLowerCase()}.`, { duration: 6000 });
      } else if (ciclo.modo === 'ordem_chegada') {
        toast(`Turno cheio — você é o ${r.posicaoFila}º da fila. Se alguém desistir, a vaga é sua.`, { icon: '⏳', duration: 7000 });
      } else {
        toast.success('Pedido enviado. A administração decide quem vai — acompanhe aqui.', { duration: 6000 });
      }
      await carregarCiclo(ciclo.id);
    } catch (e) {
      toast.error(mensagemErro(e), { duration: 7000 });
    } finally {
      setBusy(null);
    }
  };

  const pedirDesistencia = (dia, turno) => {
    const minha = turno.minha;
    setConfirm({
      titulo: minha.status === 'confirmado' ? 'Desistir da vaga?' : 'Cancelar o pedido?',
      texto:
        minha.status === 'confirmado'
          ? `Você sai da escala de ${DIAS_SEMANA[dia.diaSemana].toLowerCase()}, ${formatDataCurta(dia.data)} (${TURNO_LABEL[turno.turno].toLowerCase()}). Se pedir de novo depois, entra no fim da fila.`
          : `Seu pedido para ${formatDataCurta(dia.data)} (${TURNO_LABEL[turno.turno].toLowerCase()}) é cancelado. Você pode pedir de novo depois.`,
      label: minha.status === 'confirmado' ? 'Desistir' : 'Cancelar pedido',
      acao: async () => {
        setBusy(minha.id);
        try {
          await desistir(minha.id);
          toast.success(minha.status === 'confirmado' ? 'Você saiu da escala deste turno.' : 'Pedido cancelado.');
          setConfirm(null);
          await carregarCiclo(ciclo.id);
        } catch (e) {
          toast.error(mensagemErro(e), { duration: 7000 });
        } finally {
          setBusy(null);
        }
      },
    });
  };

  const renderTurnoItinerante = (dia, turno, t) => {
    const minha = turno.minha;
    const ativa = minha && (minha.status === 'confirmado' || minha.status === 'pendente');
    const cheio = turno.livres === 0;
    const podeAgir = aberto && ciclo.podeInscrever && !dia.passado;
    const chave = `${dia.id}:${turno.turno}`;
    const ocupado = busy === chave || (minha && busy === minha.id);

    let acao = null;
    if (turno.vagas === 0) {
      acao = null;
    } else if (ativa) {
      acao = (
        <div className={`esc-minha ${minha.status}`}>
          <span className="esc-minha-status">
            {minha.status === 'confirmado' ? (
              <><CheckCircledIcon /> Você está escalado</>
            ) : (
              <><ClockIcon /> {ciclo.modo === 'ordem_chegada' ? `Na fila (${minha.posicaoFila}º)` : 'Aguardando decisão'}</>
            )}
          </span>
          {podeAgir && (
            <button type="button" className="esc-link-btn perigo" disabled={!!busy} onClick={() => pedirDesistencia(dia, turno)}>
              {minha.status === 'confirmado' ? 'Desistir' : 'Cancelar pedido'}
            </button>
          )}
        </div>
      );
    } else if (minha?.status === 'recusado') {
      acao = (
        <div className="esc-minha recusado">
          <span className="esc-minha-status">Não selecionado</span>
          {minha.observacao && <small>{minha.observacao}</small>}
        </div>
      );
    } else if (podeAgir) {
      const rotulo =
        ciclo.modo === 'ordem_chegada' ? (cheio ? 'Entrar na fila' : 'Pegar esta vaga') : cheio ? 'Entrar na fila' : 'Pedir esta vaga';
      acao = (
        <button
          type="button"
          className={`esc-btn-vaga ${cheio ? 'fila' : ''}`}
          disabled={ocupado}
          onClick={() => pedir(dia, turno)}
        >
          {ocupado ? 'Enviando…' : rotulo}
        </button>
      );
    }

    return (
      <>
        <div className="esc-turno-cab">
          <span className="esc-turno-nome">{t.label}</span>
          {turno.vagas > 0 ? (
            <span className={`esc-cap ${cheio ? 'cheio' : ''}`}>
              {cheio ? 'lotado' : `${turno.livres} livre${turno.livres === 1 ? '' : 's'}`}
            </span>
          ) : (
            <span className="esc-cap off">—</span>
          )}
        </div>
        {turno.vagas > 0 && (
          <ul className="esc-nomes">
            {turno.confirmados.map((c) => (
              <li key={c.id} className={`esc-nome ok ${minha?.id === c.id ? 'eu' : ''}`} title={nomeEscala(c, true)}>
                {nomeEscala(c)}
              </li>
            ))}
            {Array.from({ length: turno.livres }).map((_, i) => (
              <li key={`l${i}`} className="esc-nome livre">vaga livre</li>
            ))}
          </ul>
        )}
        {turno.pendentesCount > 0 && !ativa && (
          <span className="esc-fila-badge">{turno.pendentesCount} aguardando</span>
        )}
        {acao}
      </>
    );
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Carregando…</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container esc-page">
        {ciclos.length === 0 ? (
          <>
            <div className="page-header-new">
              <div>
                <h1>Escala de Itinerantes</h1>
                <p className="subtitle">Calendário de turnos dos monitores itinerantes</p>
              </div>
            </div>
            <div className="esc-vazio">
              <div className="esc-vazio-icone"><CalendarIcon width={40} height={40} /></div>
              <h2>Nenhuma escala publicada</h2>
              <p>Quando a administração publicar o próximo ciclo, ele aparece aqui para você pedir suas vagas.</p>
            </div>
          </>
        ) : (
          <>
            <div className={`esc-hero itinerante status-${ciclo?.status || 'publicado'}`}>
              <div className="esc-hero-conteudo">
                <p className="esc-hero-sobre">Escala de Monitores Itinerantes</p>
                <h1>{ciclo?.nome || '…'}</h1>
                <div className="esc-hero-meta">
                  {ciclo && <span className="esc-hero-chip"><CalendarIcon /> {periodoLabel(ciclo)}</span>}
                  {ciclo && <span className={`esc-hero-chip status-${ciclo.status}`}>{CICLO_STATUS_LABEL[ciclo.status]}</span>}
                  {ciclo && <span className="esc-hero-chip">{MODO_LABEL[ciclo.modo]}</span>}
                </div>
                {ciclos.length > 1 && (
                  <div className="esc-ciclo-pills">
                    {ciclos.map((c) => (
                      <button key={c.id} type="button" className={`esc-ciclo-pill ${c.id === cicloId ? 'ativo' : ''}`} onClick={() => setCicloId(c.id)}>
                        {c.nome}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {ciclo && (
                <div className="esc-hero-minhas">
                  <div className="esc-hero-minha">
                    <span className="esc-hero-minha-valor">{ciclo.minhas.confirmados}</span>
                    <span className="esc-hero-minha-rotulo">turnos confirmados</span>
                  </div>
                  <div className="esc-hero-minha">
                    <span className="esc-hero-minha-valor">{ciclo.minhas.pendentes}</span>
                    <span className="esc-hero-minha-rotulo">aguardando</span>
                  </div>
                </div>
              )}
            </div>

            {carregandoCiclo && !ciclo ? (
              <div className="loading-container"><div className="loading-spinner" /></div>
            ) : ciclo ? (
              <>
                {!ciclo.podeInscrever && (
                  <div className="esc-aviso">
                    <InfoCircledIcon />
                    <span>{ciclo.motivoInscricao}</span>
                  </div>
                )}
                {ciclo.podeInscrever && ciclo.modo === 'ordem_chegada' && (
                  <div className="esc-aviso ok">
                    <StarFilledIcon />
                    <span>Ordem de chegada: quem pede primeiro fica com a vaga. Turno cheio? Entre na fila — se alguém desistir, você sobe automaticamente.</span>
                  </div>
                )}
                {ciclo.podeInscrever && ciclo.modo === 'escolha_admin' && (
                  <div className="esc-aviso ok">
                    <StarFilledIcon />
                    <span>Peça as vagas que puder cumprir. A administração escolhe quem vai em cada turno e você acompanha o resultado aqui.</span>
                  </div>
                )}
                {ciclo.observacao && (
                  <div className="esc-aviso neutro">
                    <InfoCircledIcon />
                    <span>{ciclo.observacao}</span>
                  </div>
                )}

                <section className="esc-secao">
                  <div className="esc-toolbar">
                    <p className="esc-toolbar-dica">
                      {aberto ? 'Toque no turno para pedir a vaga.' : 'Escala encerrada — somente consulta.'}
                    </p>
                    <div className="esc-toolbar-acoes">
                      <label className="esc-check">
                        <input type="checkbox" checked={mostrarFdsEfetivo} onChange={(e) => setMostrarFds(e.target.checked)} />
                        Fins de semana
                      </label>
                      <Link to={`/escala/${ciclo.id}/impressao`} className="btn-outline btn-sm esc-btn-link">
                        <FileTextIcon /> Versão para imprimir
                      </Link>
                    </div>
                  </div>
                  <EscalaGrade
                    dias={ciclo.dias}
                    turnos={turnos}
                    mostrar={mostrar}
                    renderTurno={renderTurnoItinerante}
                  />
                  <div className="esc-legenda">
                    <span><i className="esc-leg ok" /> confirmado</span>
                    <span><i className="esc-leg eu" /> você</span>
                    <span><i className="esc-leg livre" /> vaga livre</span>
                    <span><i className="esc-leg feriado" /> feriado</span>
                  </div>
                </section>

                {minhasInscricoes.length > 0 && (
                  <section className="esc-secao">
                    <h2 className="esc-secao-titulo">Meus turnos neste ciclo</h2>
                    <ul className="esc-minhas-lista">
                      {minhasInscricoes.map((m) => (
                        <li key={m.id} className={`esc-minhas-item ${m.status}`}>
                          <span className="esc-minhas-data">
                            <strong>{DIAS_SEMANA_CURTO[m.dia.diaSemana]} {formatDataCurta(m.dia.data)}</strong>
                            <span className={`esc-turno-tag esc-turno-cor-${m.turno.turno}`}>{TURNO_LABEL[m.turno.turno]}</span>
                          </span>
                          <span className="esc-minhas-status">
                            {m.status === 'confirmado' ? (
                              <><CheckCircledIcon /> Confirmado</>
                            ) : (
                              <><ClockIcon /> {ciclo.modo === 'ordem_chegada' ? `Na fila (${m.posicaoFila}º)` : 'Aguardando decisão'}</>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            ) : null}
          </>
        )}
      </div>

      <ConfirmModal
        aberto={!!confirm}
        titulo={confirm?.titulo}
        confirmLabel={confirm?.label}
        perigo
        busy={!!busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.acao()}
      >
        <p>{confirm?.texto}</p>
      </ConfirmModal>
    </>
  );
};

export default EscalaPage;
